import {
  CardType,
  Encoding,
  FieldType,
  ResponseDelimiter,
  ResponseKey,
  ResponseStatusCode,
  TransactionSubtype,
  TransactionType
} from '../../constants';
import { IFieldSchema, IMessage } from '../types';
import { mapKeyToReadableKey, prepare, tryParseValue } from '../util';

// Note: Comments are intentionally repeated in this file
// in the schema definitions - vs code can display these
// comments to consumers of the appropriate types if they are
// inlined in the schema definition, but this does not work
// if the comments are above the shared variables so please do not
// delete/remove them

// shared schema field definitions
const amount: IFieldSchema = {
  length: 10,
  type: FieldType.float
};

const authorization: IFieldSchema = {
  length: 10,
  type: FieldType.alphaNumeric
};

const creditCardNumber: IFieldSchema = {
  length: 40,
  type: FieldType.base64
};

const creditCardExpirationDate: IFieldSchema = {
  length: 4,
  type: FieldType.numeric
};

const echoLotteryStatus: IFieldSchema = {
  length: 3,
  type: FieldType.numeric
};

const operatorId: IFieldSchema = {
  length: 16,
  type: FieldType.numeric
};

const override: IFieldSchema = {
  length: 1,
  type: FieldType.alphaNumeric
};

const promoCode: IFieldSchema = {
  length: 10,
  type: FieldType.numeric
};

const secondaryAmount: IFieldSchema = {
  length: 10,
  type: FieldType.float
};

const terminalId: IFieldSchema = {
  length: 12,
  type: FieldType.alphaNumeric
};

const transactionNumber: IFieldSchema = {
  length: 12,
  type: FieldType.numeric
};

const transactionType: IFieldSchema = {
  length: 2,
  type: FieldType.alphaNumeric
};

// ordering is significant - do not sort these keys
// tslint:disable:object-literal-sort-keys
const CaptureExpandedFormatSchema = {
  /**
   * Transaction type
   *
   * @see TransactionType enum for transaction type information
   */
  transactionType,
  /**
   * transaction subtype
   * always 8 for expanded format
   */
  subType: {
    length: 1,
    type: FieldType.fixed,
    value: TransactionSubtype.ExpandedRequestSubtype
  },
  /**
   * base-64 encoded card-number or
   * single digit indicating card type
   * (optional)
   */
  creditCardNumber,
  /**
   * date the credit card expires, in MMYY format
   */
  creditCardExpirationDate,
  /**
   * amount of the transaction (in cents)
   */
  amount,
  /**
   * transaction number
   * identifier for the given order
   * must be an integer
   */
  transactionNumber,
  /**
   * terminal ID
   * used by MCM to determine pin pad identity
   */
  terminalId,
  /**
   * authorization number
   */
  authorization,
  /**
   * installment indicator
   * whether this payment is being made
   * as part of a recurring payment plan
   */
  installmentIndicator: {
    length: 1,
    type: FieldType.alphaNumeric
  },
  /**
   * cvv2 indicator
   * whether a cvv2 is being provided
   * @see Cvv2Indicator
   */
  cvv2Indicator: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * cvv2 value
   */
  cvv2Value: {
    length: 4,
    type: FieldType.numeric
  },
  /**
   * user billing zip code
   */
  zipCode: {
    length: 9,
    type: FieldType.alphaNumeric
  },
  /**
   * user billing address
   */
  billingAddress: {
    length: 35,
    type: FieldType.alphaNumeric
  },
  /**
   * name on card account
   */
  billingName: {
    length: 16,
    type: FieldType.alphaNumeric
  },
  /**
   * tax amount (in cents)
   * this will not be added to the purchase amount, it is provided
   * separately only for reporting purposes. tax should be included
   * in the value passed into the amount field
   */
  taxAmount: {
    length: 10,
    type: FieldType.float
  },
  /**
   * tip amount (in cents)
   * tip amount is treated the same way as tax amount
   * always include tip amount in the value passed into
   * the amount field
   */
  tipAmount: {
    length: 10,
    type: FieldType.float
  },
  /**
   * Card Program
   * More research is needed to understand what this value means
   * It is optional and its significance is unclear, possible
   * values are not provided in the technical specification
   */
  cardProgram: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * transaction date
   * provide in MMDDYY format, no delimiter
   */
  transactionDate: {
    length: 6,
    type: FieldType.numeric
  },
  /**
   * secondary amount
   * More research is needed to understand what this value means
   * It is optional and its significance is unclear. the documentation
   * does not provide additional information on this field
   */
  secondaryAmount,
  /**
   * promo code - optional
   * appears to be used for loyalty transactions
   */
  promoCode,
  /**
   * operator ID - optional, the ID of the operator (for reporting)
   */
  operatorId,
  /**
   * override
   * D - Demo Mode - initiate demo transaction
   * M - Override multi-messaging for current transaction
   * Note: Only works on the linux version of MCM
   */
  override,
  /**
   * transaction time
   * provide in HHMMSS format, no delimiter
   */
  transactionTime: {
    length: 6,
    type: FieldType.numeric
  },
  /**
   * Original Sequence Number
   * Note: Merchant-defined data
   */
  originalSequenceNumber: {
    length: 13,
    type: FieldType.alphaNumeric
  },
  /**
   * Customer Code/Order #
   */
  orderNumber: {
    length: 25,
    type: FieldType.alphaNumeric
  },
  /**
   * total authorization amount
   * for capturing after a pre-authorization request
   */
  totalAuthorizationAmount: {
    length: 8,
    type: FieldType.float
  },
  /**
   * original authorization amount
   * for capturing after a pre-authorization request
   */
  originalAuthorizationAmount: {
    length: 8,
    type: FieldType.float
  },
  /**
   * Adjustment/Surcharge Amount
   * for reporting purposes
   */
  adjustmentAndSurchargeAmount: {
    length: 8,
    type: FieldType.float
  },
  /**
   * Food amount
   */
  foodAmount: {
    length: 10,
    type: FieldType.float
  },
  /**
   * Merchant Defined Data field
   */
  merchantDefinedData: {
    length: 30,
    type: FieldType.alphaNumeric
  },
  /**
   * Currency Code
   */
  currencyCode: {
    length: 3,
    type: FieldType.numeric
  },
  /**
   * Currency minor unit
   */
  currencyMinorUnit: {
    length: 2,
    type: FieldType.numeric
  },
  /**
   * Currency exchange rate
   */
  currencyExchangeRate: {
    length: 13,
    type: FieldType.numeric
  },
  /**
   * Secure Auth Data (Visa/MC)
   */
  secureAuthData: {
    length: 40,
    type: FieldType.alphaNumeric
  },
  /**
   * Duplicate level checking
   * Determines whether to allow suspected duplicate
   * transactions to succeed
   */
  duplicateLevelChecking: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * e-commerce goods indicator
   * Whether the purchase is for digital
   * or physical goods
   */
  eCommerceGoodsIndicator: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * @todo definitions for charge description code, duration, special program code
   */
  empty0: {
    length: 18,
    type: FieldType.reserved
  },
  /**
   * Tax Indicator
   *
   * @see TaxIndicator
   */
  taxFlag: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * @todo add additional fields from documentation
   */
  empty1: {
    length: 48,
    type: FieldType.reserved
  },
  /**
   * Customer Name
   */
  customerName: {
    length: 30,
    type: FieldType.alphaNumeric
  },
  /**
   * echo lottery status - usage unclear
   */
  echoLotteryStatus,
  /**
   * @todo add additional fields from documentation
   */
  empty2: {
    length: 82,
    type: FieldType.reserved
  },
  /**
   * Merchant reference number
   * Can be used to provide an external identifier
   */
  merchantReferenceNumber: {
    length: 33,
    type: FieldType.alphaNumeric
  },
  /**
   * @todo add additional fields from documentation
   */
  empty3: {
    length: 89,
    type: FieldType.reserved
  },
  /**
   * Card Info Entry Mode (when card number is provided)
   * @see CardInfoEntryIndicator
   */
  cardInfoEntryMode: {
    length: 1,
    type: FieldType.numeric
  },
  /**
   * Payment Type
   * @see PaymentTypeIndicator
   */
  paymentType: {
    length: 3,
    type: FieldType.numeric
  },
  // append a '~' to the end of the message
  final: {
    length: 1,
    type: FieldType.fixed,
    value: '~'
  }
  // @todo add industry-specific field handling
};

const CaptureGiftCardValueLinkFormatSchema = {
  transactionType: {
    length: 2,
    type: FieldType.fixed,
    value: TransactionType.GiftCardRedemptionNoNSF
  },
  subtype: {
    length: 1,
    type: FieldType.fixed,
    value: TransactionSubtype.GiftCardFormat
  },
  giftCardNumber: creditCardNumber,
  userId: {
    length: 10,
    type: FieldType.alphaNumeric
  },
  password: {
    length: 10,
    type: FieldType.alphaNumeric
  },
  pinNumber: {
    length: 10,
    type: FieldType.alphaNumeric
  },
  /**
   * usage unclear
   */
  unit: {
    length: 10,
    type: FieldType.alphaNumeric
  },
  amount: {
    length: 13,
    type: FieldType.float
  },
  transactionNumber: {
    length: 17,
    type: FieldType.alphaNumeric
  },
  terminalId: {
    length: 12,
    type: FieldType.alphaNumeric
  },
  currencyCode: {
    length: 3,
    type: FieldType.alphaNumeric
  },
  originalTransactionType: {
    length: 2,
    type: FieldType.alphaNumeric
  },
  secondCardNumber: creditCardNumber,
  authorizationNumber: {
    length: 12,
    type: FieldType.alphaNumeric
  },
  operatorId,
  promoCode: {
    length: 10,
    type: FieldType.alphaNumeric
  },
  /**
   * this field appears twice in the documentation.
   * this is not a mistake.
   */
  operatorId2: operatorId,
  orderNumber: {
    length: 20,
    type: FieldType.alphaNumeric
  },
  /**
   * @see GiftCardEntryMethodIndicator
   */
  cardEntryMethod: {
    length: 1,
    type: FieldType.alphaNumeric
  },
  empty0: {
    length: 20,
    type: FieldType.reserved
  },
  numberOfCards: {
    length: 4,
    type: FieldType.numeric
  }
};
// tslint:enable:object-literal-sort-keys

export type PaymentExpandedFormatMessageInput = IMessage<
  typeof CaptureExpandedFormatSchema,
  'amount' | 'secondaryAmount' | 'terminalId' | 'transactionNumber' | 'transactionType' | 'taxFlag'
>;

export type PaymentGiftCardFormatMessageInput = IMessage<
  typeof CaptureGiftCardValueLinkFormatSchema,
  'amount' | 'terminalId' | 'transactionNumber'
>;

// gift cards have fixed transactionType so the parameters should not
// include it as a key
const isGiftCardRequest = (
  messageContent: PaymentExpandedFormatMessageInput | PaymentGiftCardFormatMessageInput
): messageContent is PaymentGiftCardFormatMessageInput => !('transactionType' in messageContent);

export function payment(
  messageContent: PaymentExpandedFormatMessageInput | PaymentGiftCardFormatMessageInput
): Buffer {
  if (isGiftCardRequest(messageContent)) {
    return prepare(messageContent, CaptureGiftCardValueLinkFormatSchema);
  }

  return prepare(messageContent, CaptureExpandedFormatSchema);
}

function parseAns(ansString: string): { ansCode: string } {
  // ans line contains <STX>ANS<STX><some response string><CR><LF>
  const [, , ansCode] = ansString.split(ResponseDelimiter.STX);

  // there is no documentation on the exact meaning of the ANS.
  // This value appears to be an alphanumeric identifier
  return { ansCode: (ansCode || '').trim() };
}

function parseRcp(rcpString: string): { result: string } {
  // RCP line contains <FS>RCP<FS>RES:<resultCode><CR><LF>
  const [, , resString] = rcpString.split(ResponseDelimiter.FS);

  const [, resultCode] = resString.split(ResponseDelimiter.Colon);

  return { result: (resultCode || '').trim() };
}

function parseReceipt(rawResponseString: string): { receipt?: string } {
  const [, , receipt] = rawResponseString.split(ResponseDelimiter.FS);

  if (!receipt) {
    return {};
  }

  return { receipt: receipt.replace(ResponseDelimiter.ETX, '') };
}

export interface IPaymentResponse {
  amountRequested: string;
  ansCode: string;
  authorizationNumber: string;
  avsResponse: string;
  cardBin: string;
  cardNumber: string;
  cashBackAmount: string;
  creditType: CardType;
  cvdResponse: string;
  date: string;
  echoLotteryStatus: string;
  expirationDate: string;
  fallbackToken: string;
  inputMethod: string;
  language: string;
  merchantId: string;
  operatorId: string;
  paymentType: string;
  rawResponse: string;
  receipt: string;
  remainingBalance: string;
  result: ResponseStatusCode;
  sequenceNumber: string;
  terminalId: string;
  time: string;
  tipAmount: string;
  token: string;
  totalTransactionAmount: string;
  transactionNumber: string;
  transactionType: string;
}

export interface IResponsePending {
  rawResponse: string;
  responsePending: true;
}

export function parseResponse(rawResponse: Buffer): IPaymentResponse | IResponsePending {
  const responseString = rawResponse.toString(Encoding.ascii);
  // first response is a string message beginning with a control char
  // \06 (ACK) is an OK
  if (responseString.startsWith(ResponseDelimiter.ACK)) {
    return {
      rawResponse: responseString.trim(),
      responsePending: true
    };
  }

  // \15 (NAK) is an Error
  if (responseString.startsWith(ResponseDelimiter.NAK)) {
    throw new Error(responseString.trim());
  }
  // all lines delimited by CRLF
  const lines = responseString.split(ResponseDelimiter.CRLF);

  const [ansLine, rcpLine, ...rest] = lines;

  const ans = parseAns(ansLine);
  const result = parseRcp(rcpLine);

  const receipt = parseReceipt(rest.join(ResponseDelimiter.CRLF));

  // rest of message are lines delimited by colons
  // i.e. <ResponseKey>:<ResponseValue>
  const restOfResultData = rest.reduce<Omit<IPaymentResponse, 'ansCode' | 'result'>>(
    (acc, line) => {
      const [key, value] = line.split(ResponseDelimiter.Colon) as [
        keyof typeof ResponseKey,
        string
      ];

      const readableKey = mapKeyToReadableKey(key);

      return {
        ...acc,
        [readableKey]: tryParseValue(readableKey, value)
      };
    },
    {} as Omit<IPaymentResponse, 'ansCode' | 'result'>
  );

  return Object.assign({}, ans, result, receipt, restOfResultData, {
    rawResponse: responseString
  }) as IPaymentResponse;
}
